import React, { useMemo, useState } from "react";
import useCurrency from "../../../../../hooks/useCurrency";

import {
    Button,
    Card,
    Divider,
    Drawer,
    Radio,
    Space,
    Statistic,
    Tag,
    Typography
} from "antd";

import {
    CheckCircleOutlined,
    CreditCardOutlined,
    EnvironmentOutlined,
    ShoppingOutlined
} from "@ant-design/icons";


const {
    Title,
    Text
} = Typography;


/**
 * TransactionDrawerOld
 *
 * Rôle :
 * - Afficher l'Agreement accepté
 * - Permettre à l'acheteur de choisir la livraison
 * - Permettre à l'acheteur de choisir le moyen de paiement
 * - Valider les choix
 *
 * IMPORTANT :
 * Ce composant ne fait volontairement aucun appel API.
 *
 * Le backend Agreement / Checkout n'ayant pas encore été
 * défini dans le code fourni, on remonte les choix au parent
 * avec onValidate.
 */
const TransactionDrawerOld = ({
    open,
    agreement,
    loading = false,
    onClose,
    onValidate
}) => {

    const [
        deliveryMethod,
        setDeliveryMethod
    ] = useState(null);


    const [
        paymentMethod,
        setPaymentMethod
    ] = useState(null);


    /*
     * ============================================================
     * AGREEMENT VALUES
     * ============================================================
     */

    const unitPrice =
        Number(
            agreement?.agreedUnitPricePerPacket ??
            agreement?.offeredUnitPricePerPacket ??
            0
        );


    const quantityPerPacket =
        Number(
            agreement?.agreedQuantityPerPacket ??
            agreement?.offeredQuantityPerPacket ??
            0
        );


    const numberOfPackets =
        Number(
            agreement?.agreedNumberOfPackets ??
            agreement?.offeredNumberOfPackets ??
            0
        );


    const { currency: contextCurrency } = useCurrency();

    const currency =
        agreement?.currency || contextCurrency;


    const measurementUnit =
        agreement?.measurementUnit || "KG";


    /*
     * ============================================================
     * TOTAL
     * ============================================================
     *
     * Prix unitaire d'un paquet × nombre de paquets.
     *
     * Exemple :
     * 15 000 XOF × 4 = 60 000 XOF
     */

    const totalAmount =
        useMemo(
            () =>
                unitPrice *
                numberOfPackets,
            [
                unitPrice,
                numberOfPackets
            ]
        );


    const formattedTotal =
        new Intl.NumberFormat(
            "fr-FR"
        ).format(totalAmount);


    const formattedUnitPrice =
        new Intl.NumberFormat(
            "fr-FR"
        ).format(unitPrice);


    /*
     * ============================================================
     * PARTICIPANT
     * ============================================================
     */

    const currentUserId =
        agreement?.currentUserId;


    const isBuyer =
        agreement?.buyerId &&
        currentUserId
            ? agreement.buyerId === currentUserId
            : true;


    const isSeller =
        agreement?.sellerId &&
        currentUserId
            ? agreement.sellerId === currentUserId
            : false;


    /*
     * ============================================================
     * VALIDATION
     * ============================================================
     */

    const canValidate =
        isBuyer &&
        Boolean(deliveryMethod) &&
        Boolean(paymentMethod);


    const handleValidate =
        () => {

            if (!canValidate) {
                return;
            }


            const payload = {

                agreementId:
                    agreement?.id,

                inquiryId:
                    agreement?.inquiryId,

                conversationId:
                    agreement?.conversationId,

                deliveryMethod,

                paymentMethod

            };


            if (onValidate) {
                onValidate(payload);
            }

        };


    /*
     * ============================================================
     * RESET WHEN DRAWER CLOSES
     * ============================================================
     */

    const handleClose =
        () => {

            setDeliveryMethod(null);
            setPaymentMethod(null);

            if (onClose) {
                onClose();
            }

        };


    /*
     * ============================================================
     * NO AGREEMENT
     * ============================================================
     */

    if (!agreement) {
        return null;
    }


    return (

        <Drawer

            title="Transaction"

            placement="right"

            width={620}

            open={open}

            onClose={handleClose}

            destroyOnClose

            styles={{
                body: {
                    padding: 20,
                    background: "#fafafa"
                }
            }}

        >

            {/* =====================================================
                HEADER
            ====================================================== */}

            <div
                style={{
                    marginBottom: 20
                }}
            >

                <Space
                    direction="vertical"
                    size={4}
                >

                    <Title
                        level={4}
                        style={{
                            margin: 0
                        }}
                    >
                        Transaction commerciale
                    </Title>

                    <Text type="secondary">
                        Accord issu de l'offre acceptée
                    </Text>

                </Space>

            </div>


            {/* =====================================================
                STATUS
            ====================================================== */}

            <Card
                size="small"
                style={{
                    marginBottom: 16,
                    borderRadius: 10
                }}
            >

                <Space>

                    <CheckCircleOutlined
                        style={{
                            color: "#52c41a",
                            fontSize: 22
                        }}
                    />

                    <div>

                        <Text strong>
                            Offre acceptée
                        </Text>

                        <br />

                        <Tag
                            color="success"
                            style={{
                                marginTop: 4
                            }}
                        >
                            ACCEPTED
                        </Tag>

                    </div>

                </Space>

            </Card>


            {/* =====================================================
                AGREEMENT
            ====================================================== */}

            <Card
                title={
                    <Space>
                        <ShoppingOutlined />
                        <span>Accord commercial</span>
                    </Space>
                }
                style={{
                    marginBottom: 20,
                    borderRadius: 10
                }}
            >

                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns:
                            "repeat(2, minmax(0, 1fr))",
                        gap: 16
                    }}
                >

                    <Statistic
                        title="Prix / paquet"
                        value={formattedUnitPrice}
                        suffix={currency}
                    />

                    <Statistic
                        title="Quantité / paquet"
                        value={quantityPerPacket}
                        suffix={measurementUnit}
                    />

                    <Statistic
                        title="Nombre de paquets"
                        value={numberOfPackets}
                        suffix="paquets"
                    />

                    <Statistic
                        title="Montant total"
                        value={formattedTotal}
                        suffix={currency}
                    />

                </div>


                <Divider
                    style={{
                        margin: "18px 0"
                    }}
                />


                <Space
                    direction="vertical"
                    size={4}
                    style={{
                        width: "100%"
                    }}
                >

                    <Text type="secondary">
                        Agreement
                    </Text>

                    <Text copyable>
                        {agreement.id}
                    </Text>

                </Space>

            </Card>


            {/* =====================================================
                DELIVERY
            ====================================================== */}

            <Card
                title={
                    <Space>
                        <EnvironmentOutlined />
                        <span>Mode de livraison</span>
                    </Space>
                }
                style={{
                    marginBottom: 20,
                    borderRadius: 10
                }}
            >

                {isBuyer ? (

                    <Radio.Group
                        value={deliveryMethod}
                        onChange={
                            event =>
                                setDeliveryMethod(
                                    event.target.value
                                )
                        }
                        style={{
                            width: "100%"
                        }}
                    >

                        <Space
                            direction="vertical"
                            size={12}
                            style={{
                                width: "100%"
                            }}
                        >

                            <Radio
                                value="DELIVERY"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        Livraison
                                    </Text>

                                    <Text type="secondary">
                                        Recevoir la commande à
                                        l'adresse indiquée.
                                    </Text>

                                </Space>
                            </Radio>


                            <Radio
                                value="SELLER_PICKUP"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        Retrait chez le vendeur
                                    </Text>

                                    <Text type="secondary">
                                        Récupérer directement la
                                        commande auprès du vendeur.
                                    </Text>

                                </Space>
                            </Radio>


                            <Radio
                                value="TRANSPORTER"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        Transporteur
                                    </Text>

                                    <Text type="secondary">
                                        Livraison par un transporteur
                                        partenaire.
                                    </Text>

                                </Space>
                            </Radio>

                        </Space>

                    </Radio.Group>

                ) : (

                    <Space
                        direction="vertical"
                        size={4}
                    >

                        <Text strong>
                            En attente du choix de l'acheteur
                        </Text>

                        <Text type="secondary">
                            L'acheteur doit sélectionner son
                            mode de livraison.
                        </Text>

                    </Space>

                )}

            </Card>


            {/* =====================================================
                PAYMENT
            ====================================================== */}

            <Card
                title={
                    <Space>
                        <CreditCardOutlined />
                        <span>Mode de paiement</span>
                    </Space>
                }
                style={{
                    marginBottom: 20,
                    borderRadius: 10
                }}
            >

                {isBuyer ? (

                    <Radio.Group
                        value={paymentMethod}
                        onChange={
                            event =>
                                setPaymentMethod(
                                    event.target.value
                                )
                        }
                        style={{
                            width: "100%"
                        }}
                    >

                        <Space
                            direction="vertical"
                            size={12}
                            style={{
                                width: "100%"
                            }}
                        >

                            <Radio
                                value="MOBILE_MONEY"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        Mobile Money
                                    </Text>

                                    <Text type="secondary">
                                        Payer avec un service
                                        Mobile Money.
                                    </Text>

                                </Space>
                            </Radio>


                            <Radio
                                value="PAYPAL"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        PayPal
                                    </Text>

                                    <Text type="secondary">
                                        Payer avec votre compte PayPal.
                                    </Text>

                                </Space>
                            </Radio>


                            <Radio
                                value="STRIPE"
                            >
                                <Space
                                    direction="vertical"
                                    size={0}
                                >

                                    <Text strong>
                                        Carte bancaire / Stripe
                                    </Text>

                                    <Text type="secondary">
                                        Paiement sécurisé via Stripe.
                                    </Text>

                                </Space>
                            </Radio>

                        </Space>

                    </Radio.Group>

                ) : (

                    <Space
                        direction="vertical"
                        size={4}
                    >

                        <Text strong>
                            En attente du paiement de l'acheteur
                        </Text>

                        <Text type="secondary">
                            Le moyen de paiement sera choisi
                            par l'acheteur.
                        </Text>

                    </Space>

                )}

            </Card>


            {/* =====================================================
                SUMMARY
            ====================================================== */}

            <Card
                size="small"
                style={{
                    marginBottom: 20,
                    borderRadius: 10
                }}
            >

                <Space
                    direction="vertical"
                    style={{
                        width: "100%"
                    }}
                >

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >

                        <Text>
                            Prix des produits
                        </Text>

                        <Text strong>
                            {formattedTotal} {currency}
                        </Text>

                    </div>


                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >

                        <Text>
                            Livraison
                        </Text>

                        <Text type="secondary">
                            À déterminer
                        </Text>

                    </div>


                    <Divider
                        style={{
                            margin: "8px 0"
                        }}
                    />


                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between"
                        }}
                    >

                        <Text strong>
                            Total
                        </Text>

                        <Text
                            strong
                            style={{
                                fontSize: 18
                            }}
                        >
                            {formattedTotal} {currency}
                        </Text>

                    </div>

                </Space>

            </Card>


            {/* =====================================================
                ACTION
            ====================================================== */}

            {isBuyer ? (

                <Button
                    type="primary"
                    size="large"
                    block
                    loading={loading}
                    disabled={!canValidate}
                    onClick={handleValidate}
                >
                    Valider mes choix
                </Button>

            ) : (

                <Button
                    size="large"
                    block
                    disabled
                >
                    En attente de la validation de l'acheteur
                </Button>

            )}


            <div
                style={{
                    marginTop: 12,
                    textAlign: "center"
                }}
            >

                <Text
                    type="secondary"
                    style={{
                        fontSize: 12
                    }}
                >
                    Le paiement sera effectué après validation
                    des informations de livraison et de paiement.
                </Text>

            </div>

        </Drawer>

    );
};


export default TransactionDrawerOld;
